import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Tooltip from "react-tooltip";
import { isMobile } from "common/utils";

const DESCRIPTIONS = {
  "give-back":
    "Give Back is our way of helping worthy non-profit and charitable causes when those organizations need design help but cannot afford it. Both crowdspring and all participating creatives waive their fees.",
  featured:
    "The client in this project purchased an optional promotion package which makes their project more visible to the crowdspring community and typically attracts more participants and entries.",
  elite:
    "Elite projects allow clients to work with 5 of the very best designers on crowdspring (those with a reputation of 90 and above).",
  platinum:
    "This project is only for our top level creatives (reputation of 80 to 100). Clients in Platinum projects receive premium entries from our best creatives",
  nda: "When a client requires an non-disclosure agreement (NDA) in a project, creatives will have access to a limited public project description created by the client until they sign the NDA. After signing the NDA, they will gain access to the full project brief and be permitted to submit entries.",
  blind:
    "Some clients want to protect the privacy of their new product or service. We offer them the option of making their project galleries completely private to hide the entries from prying eyes.",
  hidden:
    "We sometimes have to put a project down for a nap (hide it) when we have to clarify something with the client.",
  assured:
    "By assuring their project, a client waives their right to a refund and promises to award a winner.",
  "fast-tracked":
    "Fast-tracked projects last 1, 2, or 3 days, not including the optional Finalist Round.",
  "human-created":
    "Every entry is made by a human, not generated by artificial intelligence. You're seeing 100% human creativity, no AI-generated content here.",
};

const ProjectTags = ({ project, className, showColors, showTooltips }) => {
  const tagClasses = showColors
    ? "tag tag-sm tag-highlight"
    : "tag tag-sm tag-default";
  const getTooltipId = (name) => `tooltip-${name}-${project.id}`;
  const mobile = isMobile();

  const renderTag = ({ name, title }) => {
    return (
      <span
        className={cn(tagClasses, name)}
        data-tip={true}
        data-for={getTooltipId(name)}
        data-event={mobile ? "click" : undefined}
      >
        {title}
      </span>
    );
  };

  return (
    <div className={className}>
      {renderTag({
        name: "human-created",
        title: (
          <>
            <i className="fa-regular fa-head-side-brain" /> Human-created
          </>
        ),
      })}

      {project.give_back &&
        renderTag({ name: "give-back", title: "Give Back" })}

      {project.featured && renderTag({ name: "featured", title: "Featured" })}

      {project.elite_only && renderTag({ name: "elite", title: "Elite" })}

      {project.platinum_only &&
        renderTag({ name: "platinum", title: "Platinum" })}

      {project.nda_required &&
        renderTag({
          name: "nda",
          title: (
            <>
              <i className="fa fa-lock" /> NDA
            </>
          ),
        })}

      {project.private_gallery &&
        renderTag({
          name: "blind",
          title: (
            <>
              <i className="fa fa-eye-slash" /> Blind
            </>
          ),
        })}

      {!project.visible &&
        renderTag({
          name: "hidden",
          title: (
            <>
              <i className="fa fa-eye-slash" /> Hidden
            </>
          ),
        })}

      {project.assured && renderTag({ name: "assured", title: "Assured" })}

      {project.fast_tracked &&
        renderTag({ name: "fast-tracked", title: "Fast-tracked" })}

      {showTooltips &&
        Object.keys(DESCRIPTIONS).map((name) => (
          <Tooltip
            key={name}
            class="tooltip"
            effect="solid"
            place="bottom"
            id={getTooltipId(name)}
          >
            {DESCRIPTIONS[name]}
          </Tooltip>
        ))}
    </div>
  );
};

ProjectTags.defaultProps = {
  showColors: true,
};

ProjectTags.propTypes = {
  project: PropTypes.object.isRequired,
  className: PropTypes.string,
  showColors: PropTypes.bool,
  showTooltips: PropTypes.bool,
};

export default ProjectTags;
